import { computed } from 'vue';
import { permissionsService } from '../services/permissionsService';

/**
 * Composable for payroll permission checks
 * Implements the permission matrix from backend requirements
 */
export function usePayrollPermissions() {
  const userGroups = computed(() => {
    return permissionsService.getUserGroups() || [];
  });

  const accessibleFranchises = computed(() => {
    return (permissionsService as any).getAccessibleFranchises() || [];
  });

  const isFull = computed(() => userGroups.value.includes('Full'));
  const isFranqueado = computed(() => userGroups.value.includes('Franqueado'));
  const isGerente = computed(() => userGroups.value.includes('Gerente'));
  const isAtendente = computed(() => userGroups.value.includes('Atendente'));
  const isOperador = computed(() => userGroups.value.includes('Operador'));

  // Create payroll period permission (Full, Franqueado only)
  const canCreatePayrollPeriod = computed(() => {
    return isFull.value || isFranqueado.value;
  });

  // Edit payroll period permission (Full, Franqueado only)
  const canEditPayrollPeriod = computed(() => {
    return isFull.value || isFranqueado.value;
  });

  // Add deductions permission (Full, Franqueado, Gerente)
  const canAddDeductions = computed(() => {
    return isFull.value || isFranqueado.value || isGerente.value;
  });

  // Upload receipts permission (Full, Franqueado, Gerente)
  const canUploadReceipts = computed(() => {
    return isFull.value || isFranqueado.value || isGerente.value;
  });

  // View payroll periods permission (all except Operador)
  const canViewPayrollPeriods = computed(() => {
    return isFull.value || isFranqueado.value || isGerente.value || isAtendente.value;
  });

  // View own payments permission (all roles)
  const canViewOwnPayments = computed(() => {
    return true; // All users can view their own payments
  });

  // Generate reports permission (Full, Franqueado, Gerente)
  const canGenerateReports = computed(() => {
    return isFull.value || isFranqueado.value || isGerente.value;
  });

  // Manage payment rules permission (Full, Franqueado, Gerente)
  const canManagePaymentRules = computed(() => {
    return isFull.value || isFranqueado.value || isGerente.value;
  });

  // Complete payment permission (Full, Franqueado only)
  const canCompletePayment = computed(() => {
    return isFull.value || isFranqueado.value;
  });

  // Reverse payroll permission (Full, Franqueado only)
  const canReversePayroll = computed(() => {
    return isFull.value || isFranqueado.value;
  });

  // Archive payroll permission (Full, Franqueado only)
  const canArchivePayroll = computed(() => {
    return isFull.value || isFranqueado.value;
  });

  /**
   * Check if user can access a specific franchise
   * @param {number} franchiseId
   * @returns {boolean}
   */
  const canAccessFranchise = (franchiseId: number) => {
    if (isFull.value) return true;
    return accessibleFranchises.value.some((f: any) => f.id === franchiseId || f.franquia_id === franchiseId);
  };

  /**
   * Get franchises user has access to
   * @returns {Array}
   */
  const getAccessibleFranchiseIds = () => {
    if (isFull.value) {
      // Full group has access to all franchises
      return accessibleFranchises.value.map((f: any) => f.id || f.franquia_id);
    }
    return accessibleFranchises.value.map((f: any) => f.id || f.franquia_id);
  };

  return {
    // Role checks
    isFull,
    isFranqueado,
    isGerente,
    isAtendente,
    isOperador,
    userGroups,
    accessibleFranchises,

    // Permission checks
    canCreatePayrollPeriod,
    canEditPayrollPeriod,
    canAddDeductions,
    canUploadReceipts,
    canViewPayrollPeriods,
    canViewOwnPayments,
    canGenerateReports,
    canManagePaymentRules,
    canCompletePayment,
    canReversePayroll,
    canArchivePayroll,

    // Franchise access
    canAccessFranchise,
    getAccessibleFranchiseIds
  };
}
